type ScannerOverlayOptions = {
  onCapture: (file: File) => Promise<void> | void
  onClose?: () => void
}

export class ScannerOverlay {
  private root: HTMLElement
  private options: ScannerOverlayOptions
  private overlayEl: HTMLDivElement | null = null
  private videoEl: HTMLVideoElement | null = null
  private canvasEl: HTMLCanvasElement | null = null
  private stream: MediaStream | null = null

  constructor(root: HTMLElement, options: ScannerOverlayOptions) {
    this.root = root
    this.options = options
    void this.init()
  }

  private async init(): Promise<void> {
    this.createOverlay()
    try {
      await this.startCamera()
    } catch (err) {
      console.error('Error inicializando cámara o motor de escaneo:', err)
      alert('No se pudo iniciar la cámara. Revisa los permisos del navegador.')
      this.destroy()
    }
  }

  private createOverlay(): void {
    const overlay = document.createElement('div')
    overlay.style.position = 'fixed'
    overlay.style.inset = '0'
    overlay.style.zIndex = '9999'
    overlay.style.background = 'rgba(0,0,0,0.85)'
    overlay.style.display = 'flex'
    overlay.style.flexDirection = 'column'
    overlay.style.alignItems = 'center'
    overlay.style.justifyContent = 'center'
    overlay.style.padding = '1rem'
    overlay.style.overflow = 'hidden'

    overlay.innerHTML = `
      <div style="width: 100%; max-width: 900px; height: 100%; background: #000; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; border: 1px solid rgba(255,255,255,0.15); box-shadow: 0 12px 40px rgba(0,0,0,0.5);">
        <header style="padding: 0.75rem 1rem; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(90deg, rgba(15,23,42,0.95), rgba(30,64,175,0.9)); color: white;">
          <div>
            <h3 style="margin: 0; font-size: 1rem;">Escáner de Facturas</h3>
            <p style="margin: 0.1rem 0 0 0; font-size: 0.8rem; opacity: 0.9;">Alinea la factura con el marco para una mejor detección.</p>
          </div>
          <button id="scanner-overlay-close" style="background: transparent; border: none; color: white; font-size: 1.2rem; cursor: pointer;">✕</button>
        </header>
        <div style="position: relative; flex: 1; min-height: 0; background: #000;">
          <video id="scanner-overlay-video" autoplay playsinline muted style="width: 100%; height: 100%; object-fit: cover;"></video>
          <canvas id="scanner-overlay-canvas" style="position: absolute; inset: 0; pointer-events: none;"></canvas>
          <div style="position: absolute; inset: 12%; border-radius: 16px; border: 2px dashed rgba(255,255,255,0.4); pointer-events: none;"></div>
        </div>
        <footer id="scanner-overlay-footer" style="padding: 0.75rem 1rem; background: rgba(15,23,42,0.98); color: white; display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;">
          <div id="scanner-overlay-hint" style="font-size: 0.8rem; opacity: 0.9;">
            <div>Conforme el marco blanco se ajuste a los bordes de la factura, pulsa "Capturar".</div>
          </div>
          <div id="scanner-overlay-actions" style="display: flex; gap: 0.5rem;">
            <button id="scanner-overlay-cancel" style="background: transparent; border: 1px solid rgba(148,163,184,0.8); color: #e5e7eb; padding: 0.4rem 0.9rem; border-radius: 999px; font-size: 0.85rem; cursor: pointer;">Cancelar</button>
            <button id="scanner-overlay-capture" style="background: #22c55e; border: none; color: #022c22; padding: 0.4rem 1.2rem; border-radius: 999px; font-size: 0.9rem; font-weight: 600; cursor: pointer; box-shadow: 0 0 0 1px rgba(34,197,94,0.4), 0 10px 30px rgba(22,163,74,0.5);">Capturar</button>
          </div>
        </footer>
      </div>
    `

    this.root.appendChild(overlay)
    this.overlayEl = overlay
    this.videoEl = overlay.querySelector<HTMLVideoElement>('#scanner-overlay-video')
    this.canvasEl = overlay.querySelector<HTMLCanvasElement>('#scanner-overlay-canvas')

    const footer = overlay.querySelector<HTMLElement>('#scanner-overlay-footer')
    const actions = overlay.querySelector<HTMLElement>('#scanner-overlay-actions')

    overlay.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return

      const closeBtn = target.closest<HTMLButtonElement>('#scanner-overlay-close')
      const cancelBtn = target.closest<HTMLButtonElement>('#scanner-overlay-cancel')
      const captureBtn = target.closest<HTMLButtonElement>('#scanner-overlay-capture')

      if (closeBtn) {
        console.log('[ScannerOverlay] Close button clicked')
        event.preventDefault()
        event.stopPropagation()
        this.destroy()
        return
      }

      if (cancelBtn) {
        console.log('[ScannerOverlay] Cancel button clicked')
        event.preventDefault()
        event.stopPropagation()
        this.destroy()
        return
      }

      if (captureBtn) {
        console.log('[ScannerOverlay] Capture button clicked')
        event.preventDefault()
        event.stopPropagation()
        void this.handleCapture()
      }
    })

    // Ajustes básicos para móviles: botones visibles y a ancho completo
    if (footer && actions && window.innerWidth <= 640) {
      footer.style.flexDirection = 'column'
      footer.style.alignItems = 'stretch'
      const hint = overlay.querySelector<HTMLElement>('#scanner-overlay-hint')
      if (hint) {
        hint.style.textAlign = 'center'
        hint.style.marginBottom = '0.5rem'
      }
      actions.style.width = '100%'
      actions.style.justifyContent = 'center'
      actions.style.gap = '0.75rem'
      const actionButtons = actions.querySelectorAll<HTMLButtonElement>('button')
      actionButtons.forEach((btn) => {
        btn.style.flex = '1 1 0'
        btn.style.width = '100%'
        btn.style.padding = '0.6rem 1rem'
        btn.style.fontSize = '0.95rem'
      })
    }
  }

  private async startCamera(): Promise<void> {
    if (!this.videoEl) throw new Error('Video element not found')

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: false
    })

    this.videoEl.srcObject = this.stream

    await new Promise<void>((resolve) => {
      if (!this.videoEl) return resolve()
      this.videoEl.onloadedmetadata = () => resolve()
    })

    if (this.canvasEl && this.videoEl) {
      this.canvasEl.width = this.videoEl.videoWidth
      this.canvasEl.height = this.videoEl.videoHeight
    }
  }

  private async handleCapture(): Promise<void> {
    if (!this.videoEl) return

    try {
      console.log('[ScannerOverlay] handleCapture start')
      const video = this.videoEl

      // Captura directa del frame de vídeo a un canvas 2D (sin warp, máxima compatibilidad)
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth || video.clientWidth
      canvas.height = video.videoHeight || video.clientHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('No se pudo obtener contexto 2D para captura')
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      const file: File = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo generar blob de la imagen'))
              return
            }
            const f = new File([blob], 'factura-capturada.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now()
            })
            resolve(f)
          },
          'image/jpeg',
          0.95
        )
      })

      console.log('[ScannerOverlay] calling onCapture with file', file.name, file.size)
      await this.options.onCapture(file)
      this.destroy()
    } catch (err) {
      console.error('Error al capturar y procesar frame:', err)
      alert('No se pudo capturar la imagen. Intenta de nuevo.')
    }
  }

  private stopCamera(): void {
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
  }

  private destroy(): void {
    this.stopCamera()
    if (this.overlayEl?.parentElement) {
      this.overlayEl.parentElement.removeChild(this.overlayEl)
    }
    this.overlayEl = null
    this.videoEl = null
    this.canvasEl = null
    if (this.options.onClose) {
      this.options.onClose()
    }
  }
}

